/**
 * WhatsAppContent - Integração WhatsApp/IA: conexão, IA, saudação/ausência, status (stories)
 * Extraído VERBATIM do App.js (Fase 2 da refatoração — zero mudança de comportamento).
 * Inclui os auxiliares WEEKDAYS/emptyStatusForm e o subcomponente StatusPostsManager
 * (usados exclusivamente por esta seção).
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { AlertCircle, Bell, Calendar, CheckCircle, Edit, Image as ImageIcon, Key, MessageSquare, Moon, Plus, RefreshCw, Send, Trash2, Upload, X, XCircle, Zap } from 'lucide-react';
import StatusMessagesPanel from '../../StatusMessagesPanel';
import GreetingMessageSettings from '../GreetingMessageSettings';
import AwayMessageSettings from '../AwayMessageSettings';
import { getBackendUrl } from '../../utils/api';
import StatusPostsManager from './whatsapp/StatusPostsManager';
import WhatsAppAITab from './whatsapp/WhatsAppAITab';

const API = `${getBackendUrl()}/api`;

// WhatsApp Content Component
const WhatsAppContent = () => {
  const [activeTab, setActiveTab] = useState('connection');
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [qrCode, setQrCode] = useState(null);
  const [connecting, setConnecting] = useState(false);
  const [pairingPhone, setPairingPhone] = useState('');
  const [selectedUF, setSelectedUF] = useState('');
  const [selectedCity, setSelectedCity] = useState('');
  const [proxyCities, setProxyCities] = useState([]);
  const [loadingCities, setLoadingCities] = useState(false);
  const [pairingCode, setPairingCode] = useState(null);
  const [copiedPairing, setCopiedPairing] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [refreshingStatus, setRefreshingStatus] = useState(false);
  const [testMessage, setTestMessage] = useState({ number: '', text: 'Teste de mensagem do Pede é Fácil! 🚀' });
  const [sendingTest, setSendingTest] = useState(false);
  const [pollingInterval, setPollingInterval] = useState(null);
  
  // AI Config states
  const [aiConfig, setAiConfig] = useState({
    ai_provider: 'pedeefacil',
    ai_api_key: '',
    ai_model: 'pedeefacil-default',
    personality_prompt: `Lembre-se sempre que antes da primeira mensagem que você enviar o cliente já recebeu a mensagem padrão de saudação, então sua primeira mensagem deve ser algo simples como: "se precisar de ajuda, estou aqui!"

Seja extremamente objetiva nas respostas. Evite textos longos, sua função é responder dúvidas de forma clara e objetiva.

Caso o cliente não consiga fazer o pedido pelo cardápio digital, você tem autorização pra recolher pedidos no whatsapp. Depois do cliente responder, recolha pedido. Depois do cliente responder, endereço de entrega. E depois do cliente responder a última dúvida, pergunte forma de pagamento, e por último peça para que o cliente confirme o pedido e salve no sistema interno.

Converse como uma pessoa normal, de forma clara e curta. Cada pergunta deve ter no máximo 10 palavras, não pergunte tudo de uma única vez. Fale como uma pessoa, nada de robotizado.

Não pergunte quantos produtos o cliente deseja. Tente agir como um humano desleixado agiria.`,
    enabled: true,
    menu_image_url: '',
    menu_images: [],  // Array de até 6 imagens
    send_menu_images_enabled: true,  // Toggle para habilitar/desabilitar envio quando o cliente pedir
    send_greeting_images_enabled: false,  // Toggle para enviar imagens junto com a saudação
    response_delay: 15,  // Delay de resposta em segundos (padrão: 15s)
    mode: 'active',  // 'active' | 'passive' | 'off'
    passive_trigger_words: [],  // Palavras-gatilho para escuta passiva
    suppress_ai_after_digital_order: false,  // Silenciar IA após pedido no cardápio digital (4h)
  });
  const [savedAiConfig, setSavedAiConfig] = useState(null);
  const [savingAi, setSavingAi] = useState(false);
  const [showAiKey, setShowAiKey] = useState(false);
  const [reloadingAi, setReloadingAi] = useState(false);
  const [uploadingMenuImage, setUploadingMenuImage] = useState(false);

  useEffect(() => {
    loadStatus();
    loadAiConfig();
    
    // Cleanup quando sair da aba WhatsApp
    return () => {
      // Parar o polling
      if (pollingInterval) {
        clearInterval(pollingInterval);
      }
      // Nota: O QR code será limpo automaticamente pois o componente é desmontado
      // Quando voltar, o loadStatus() será chamado novamente
    };
  }, []);
  
  // Limpar QR code quando mudar de sub-aba (connection -> outras)
  useEffect(() => {
    if (activeTab !== 'connection') {
      // Usuário saiu da aba de conexão, limpar QR code
      setQrCode(null);
      if (pollingInterval) {
        clearInterval(pollingInterval);
        setPollingInterval(null);
      }
    }
  }, [activeTab]);

  const loadStatus = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/whatsapp/status`);
      setStatus(response.data);
      
      // NÃO carregar QR code automaticamente do loadStatus
      // O QR code só deve ser mostrado quando o usuário clicar em "Conectar" ou "Reconectar"
      // Isso evita mostrar QR codes antigos/desatualizados
      
      // Se conectou, parar polling
      if (response.data.connected && pollingInterval) {
        clearInterval(pollingInterval);
        setPollingInterval(null);
      }
    } catch (error) {
      console.error('Error loading status:', error);
      setStatus({ connected: false, status: 'disconnected' });
    } finally {
      setLoading(false);
    }
  };

  const startPolling = () => {
    // Parar polling anterior se existir
    if (pollingInterval) {
      clearInterval(pollingInterval);
    }
    
    console.log('🔄 Iniciando polling de status...');
    const interval = setInterval(async () => {
      try {
        const response = await axios.get(`${API}/whatsapp/status`);
        console.log('📊 Status polling:', response.data);
        setStatus(response.data);
        
        // Se recebemos um QR code na resposta de status, atualizar o estado
        if (response.data.status === 'qrcode' && response.data.qr_code) {
          setQrCode(prevQrCode => {
            // Atualizar se não temos QR code ou se é diferente
            if (!prevQrCode || prevQrCode !== response.data.qr_code) {
              console.log('📱 QR Code recebido/atualizado via polling');
              return response.data.qr_code;
            }
            return prevQrCode;
          });
        }
        if (response.data.pairing_code) {
          setPairingCode(response.data.pairing_code);
        }
        
        if (response.data.connected) {
          console.log('✅ WhatsApp conectado! Parando polling.');
          clearInterval(interval);
          setPollingInterval(null);
          setQrCode(null);
          setPairingCode(null);
          toast.success('✅ WhatsApp conectado com sucesso!');
        }
      } catch (error) {
        console.error('❌ Polling error:', error);
      }
    }, 1500); // Check every 1.5 seconds for faster response
    
    setPollingInterval(interval);
  };

  const BR_UFS = ['AC','AL','AP','AM','BA','CE','DF','ES','GO','MA','MT','MS','MG','PA','PB','PR','PE','PI','RJ','RN','RS','RO','RR','SC','SP','SE','TO'];

  const loadCities = async (uf) => {
    setSelectedCity(''); setProxyCities([]);
    if (!uf) return;
    try {
      setLoadingCities(true);
      const resp = await axios.get(`${API}/whatsapp/proxy-cities?state=${uf.toLowerCase()}`);
      setProxyCities(resp.data.cities || []);
    } catch (error) {
      const detail = error.response?.data?.detail || 'Não foi possível carregar as cidades. Tente novamente.';
      toast.error(detail);
    } finally {
      setLoadingCities(false);
    }
  };

  const handleConnect = async (phone = null) => {
    if (!selectedUF || !selectedCity) {
      toast.error('Selecione seu Estado e Cidade antes de conectar.');
      return;
    }
    try {
      setConnecting(true);
      setQrCode(null);
      setPairingCode(null);
      const payload = { proxy_state: selectedUF.toLowerCase(), proxy_city: selectedCity };
      if (phone) payload.phone = phone;
      const response = await axios.post(`${API}/whatsapp/connect`, payload);
      const hasPairing = !!response.data.pairing_code;
      if (response.data.qr_code || hasPairing) {
        if (response.data.qr_code) setQrCode(response.data.qr_code);
        if (hasPairing) setPairingCode(response.data.pairing_code);
        setStatus({ connected: false, status: 'qrcode' });
        if (response.data.pairing_fallback) {
          toast.warning(response.data.message || 'Use o QR Code para conectar.');
        } else {
          toast.success(hasPairing ? 'Código de pareamento gerado!' : 'QR Code gerado! Escaneie com o WhatsApp');
        }
        startPolling();
      } else {
        setStatus({ connected: false, status: 'qrcode' });
        startPolling();
        toast.info('Aguarde o QR Code ser gerado...');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao conectar WhatsApp');
    } finally {
      setConnecting(false);
    }
  };

  const handlePairingConnect = () => {
    const digits = (pairingPhone || '').replace(/\D/g, '');
    if (digits.length < 10) {
      toast.error('Informe um número válido com DDD (ex: 11 99999-9999)');
      return;
    }
    handleConnect(digits);
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Deseja realmente desconectar o WhatsApp?')) return;
    
    try {
      setDisconnecting(true);
      await axios.delete(`${API}/whatsapp/disconnect`);
      
      if (pollingInterval) {
        clearInterval(pollingInterval);
        setPollingInterval(null);
      }
      
      setQrCode(null);
      setStatus({ connected: false, status: 'disconnected' });
      toast.success('WhatsApp desconectado com sucesso');
    } catch (error) {
      console.error('Error disconnecting:', error);
      toast.error('Erro ao desconectar');
    } finally {
      setDisconnecting(false);
    }
  };

  const handleSendTest = async () => {
    if (!testMessage.number || !testMessage.text) {
      toast.error('Preencha o número e a mensagem');
      return;
    }
    
    try {
      setSendingTest(true);
      await axios.post(`${API}/whatsapp/send-test`, testMessage);
      toast.success('✅ Mensagem enviada com sucesso!');
      setTestMessage({ number: '', text: 'Teste de mensagem do Pede é Fácil! 🚀' });
    } catch (error) {
      console.error('Error sending test:', error);
      toast.error(error.response?.data?.detail || 'Erro ao enviar mensagem');
    } finally {
      setSendingTest(false);
    }
  };

  // handleReconnect removido: o fluxo unificado usa handleConnect (QR Code + código de pareamento)

  // Forçar sincronização imediata com a UazAPI (botão "Já escaneei")
  const handleRefreshStatus = async () => {
    try {
      setRefreshingStatus(true);
      const r = await axios.post(`${API}/whatsapp/refresh-status`);
      const data = r.data || {};
      if (data.connected) {
        toast.success('✅ WhatsApp conectado!');
        setQrCode(null);
        setPairingCode(null);
        if (pollingInterval) { clearInterval(pollingInterval); setPollingInterval(null); }
        await loadStatus();
      } else if (data.checked) {
        toast.info(data.message || 'Ainda não conectou. Escaneie o QR e tente de novo.');
      } else {
        toast.warning(data.message || 'Não foi possível verificar agora.');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao verificar conexão.');
    } finally {
      setRefreshingStatus(false);
    }
  };



  // AI Configuration functions
  const loadAiConfig = async () => {
    try {
      const response = await axios.get(`${API}/whatsapp/ai-config`);
      setSavedAiConfig(response.data);
      
      if (response.data.configured) {
        setAiConfig(prev => ({
          ...prev,
          ai_provider: response.data.ai_provider,
          ai_model: response.data.ai_model,
          personality_prompt: response.data.personality_prompt,
          enabled: response.data.enabled,
          menu_image_url: response.data.menu_image_url || '',
          menu_images: response.data.menu_images || [],
          send_menu_images_enabled: response.data.send_menu_images_enabled !== false,
          send_greeting_images_enabled: response.data.send_greeting_images_enabled === true,
          response_delay: response.data.response_delay ?? 15,
          mode: response.data.mode || 'active',
          passive_trigger_words: response.data.passive_trigger_words || [],
          suppress_ai_after_digital_order: response.data.suppress_ai_after_digital_order === true,
        }));
      }
    } catch (error) {
      console.error('Error loading AI config:', error);
    }
  };

  const handleSaveAiConfig = async () => {
    try {
      setSavingAi(true);
      const payload = {
        ai_provider: aiConfig.ai_provider,
        ai_model: aiConfig.ai_model,
        personality_prompt: aiConfig.personality_prompt,
        enabled: aiConfig.enabled,
        menu_image_url: aiConfig.menu_images?.length > 0 ? aiConfig.menu_images[0] : null,  // Legacy: null se não tem imagens
        menu_images: aiConfig.menu_images || [],  // Novo: array de imagens (pode ser vazio)
        send_menu_images_enabled: aiConfig.send_menu_images_enabled !== false,  // Toggle para envio quando o cliente pede
        send_greeting_images_enabled: aiConfig.send_greeting_images_enabled === true,  // Toggle para envio na saudação
        response_delay: aiConfig.response_delay ?? 15,  // Delay de resposta em segundos (0 = instantâneo)
        mode: aiConfig.mode || 'active',
        passive_trigger_words: aiConfig.passive_trigger_words || [],
        suppress_ai_after_digital_order: aiConfig.suppress_ai_after_digital_order === true,
      };
      
      // Include API key only if provided
      if (aiConfig.ai_api_key && aiConfig.ai_api_key.trim() !== '') {
        payload.ai_api_key = aiConfig.ai_api_key;
      } else if (savedAiConfig?.ai_api_key_masked) {
        // If already has a key saved, don't overwrite
        payload.ai_api_key = savedAiConfig.ai_api_key_masked.includes('*') ? undefined : savedAiConfig.ai_api_key_masked;
      }
      
      // Remove undefined fields
      Object.keys(payload).forEach(key => payload[key] === undefined && delete payload[key]);
      
      await axios.post(`${API}/whatsapp/ai-config`, payload);
      toast.success('✅ Configurações de IA salvas com sucesso!');
      await loadAiConfig();
      setAiConfig(prev => ({ ...prev, ai_api_key: '' }));
      setShowAiKey(false);
    } catch (error) {
      console.error('Error saving AI config:', error);
      toast.error(error.response?.data?.detail || 'Erro ao salvar configurações');
    } finally {
      setSavingAi(false);
    }
  };

  const handleReloadAi = async (clearHistory = false) => {
    const confirmMessage = clearHistory
      ? '🔄 Isso irá reiniciar a IA e limpar TODO o histórico de conversas. Deseja continuar?'
      : '🔄 Isso irá reiniciar a IA para aplicar as novas configurações. Deseja continuar?';
    
    if (!window.confirm(confirmMessage)) return;

    try {
      setReloadingAi(true);
      const response = await axios.post(`${API}/whatsapp/ai-reload?clear_history=${clearHistory}`);
      
      if (clearHistory) {
        toast.success('✅ IA reiniciada e histórico limpo!');
      } else {
        toast.success('✅ IA atualizada! Mudanças aplicadas.');
      }
      
      console.log('AI Reload Stats:', response.data.stats);
    } catch (error) {
      console.error('Error reloading AI:', error);
      toast.error(error.response?.data?.detail || 'Erro ao reiniciar IA');
    } finally {
      setReloadingAi(false);
    }
  };



  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <svg viewBox="0 0 24 24" className="w-9 h-9 sm:w-10 sm:h-10 shrink-0" fill="#25D366" aria-hidden="true">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.885-9.885 9.885M20.52 3.449C18.24 1.245 15.24 0 12.045 0 5.463 0 .104 5.334.101 11.893c0 2.096.549 4.142 1.595 5.945L0 24l6.335-1.652a12.062 12.062 0 005.71 1.447h.006c6.585 0 11.946-5.335 11.949-11.896 0-3.176-1.24-6.165-3.495-8.411" />
        </svg>
        <div>
          <h1 className="text-3xl font-bold text-white">WhatsApp</h1>
          <p className="text-gray-400 mt-1">Conecte seu WhatsApp e configure a IA</p>
        </div>
      </div>

      {/* Tabs - Layout responsivo para mobile */}
      <div className="glass-card p-2 sm:p-0">
        {/* Mobile: Grid 3x2 */}
        <div className="grid grid-cols-3 gap-2 sm:hidden">
          <button
            onClick={() => setActiveTab('connection')}
            className={`flex flex-col items-center justify-center p-4 rounded-xl transition-all ${
              activeTab === 'connection'
                ? 'bg-green-500/20 border-2 border-green-500 text-green-400'
                : 'bg-gray-800/50 border-2 border-transparent text-gray-400 hover:bg-gray-700/50'
            }`}
          >
            <MessageSquare size={28} className="mb-2" />
            <span className="text-xs font-medium text-center">Conexão</span>
          </button>
          <button
            onClick={() => setActiveTab('ai')}
            className={`flex flex-col items-center justify-center p-4 rounded-xl transition-all ${
              activeTab === 'ai'
                ? 'bg-purple-500/20 border-2 border-purple-500 text-purple-400'
                : 'bg-gray-800/50 border-2 border-transparent text-gray-400 hover:bg-gray-700/50'
            }`}
          >
            <Zap size={28} className="mb-2" />
            <span className="text-xs font-medium text-center">Config. IA</span>
          </button>
          <button
            onClick={() => setActiveTab('greeting')}
            className={`flex flex-col items-center justify-center p-4 rounded-xl transition-all ${
              activeTab === 'greeting'
                ? 'bg-blue-500/20 border-2 border-blue-500 text-blue-400'
                : 'bg-gray-800/50 border-2 border-transparent text-gray-400 hover:bg-gray-700/50'
            }`}
          >
            <MessageSquare size={28} className="mb-2" />
            <span className="text-xs font-medium text-center">Saudação</span>
          </button>
          <button
            onClick={() => setActiveTab('away')}
            className={`flex flex-col items-center justify-center p-4 rounded-xl transition-all ${
              activeTab === 'away'
                ? 'bg-amber-500/20 border-2 border-amber-500 text-amber-400'
                : 'bg-gray-800/50 border-2 border-transparent text-gray-400 hover:bg-gray-700/50'
            }`}
          >
            <Moon size={28} className="mb-2" />
            <span className="text-xs font-medium text-center">Ausência</span>
          </button>
          <button
            data-testid="whatsapp-tab-status-messages-mobile"
            onClick={() => setActiveTab('status-messages')}
            className={`flex flex-col items-center justify-center p-4 rounded-xl transition-all ${
              activeTab === 'status-messages'
                ? 'bg-emerald-500/20 border-2 border-emerald-500 text-emerald-400'
                : 'bg-gray-800/50 border-2 border-transparent text-gray-400 hover:bg-gray-700/50'
            }`}
          >
            <Bell size={28} className="mb-2" />
            <span className="text-xs font-medium text-center">Mensagens</span>
          </button>
          <button
            data-testid="whatsapp-tab-status-posts-mobile"
            onClick={() => setActiveTab('status-posts')}
            className={`flex flex-col items-center justify-center p-4 rounded-xl transition-all ${
              activeTab === 'status-posts'
                ? 'bg-pink-500/20 border-2 border-pink-500 text-pink-400'
                : 'bg-gray-800/50 border-2 border-transparent text-gray-400 hover:bg-gray-700/50'
            }`}
          >
            <Calendar size={28} className="mb-2" />
            <span className="text-xs font-medium text-center">Status</span>
          </button>
        </div>

        {/* Desktop: Tabs horizontais */}
        <div className="hidden sm:flex border-b border-gray-700">
          <button
            onClick={() => setActiveTab('connection')}
            className={`flex-1 px-6 py-4 font-medium transition-all ${
              activeTab === 'connection'
                ? 'text-white border-b-2 border-green-500 bg-green-500/10'
                : 'text-gray-400 hover:text-gray-300 hover:bg-gray-800/50'
            }`}
          >
            <div className="flex items-center justify-center gap-2">
              <MessageSquare size={20} />
              <span>Conexão WhatsApp</span>
            </div>
          </button>
          <button
            onClick={() => setActiveTab('ai')}
            className={`flex-1 px-6 py-4 font-medium transition-all ${
              activeTab === 'ai'
                ? 'text-white border-b-2 border-purple-500 bg-purple-500/10'
                : 'text-gray-400 hover:text-gray-300 hover:bg-gray-800/50'
            }`}
          >
            <div className="flex items-center justify-center gap-2">
              <Zap size={20} />
              <span>Configurar IA</span>
            </div>
          </button>
          <button
            onClick={() => setActiveTab('greeting')}
            className={`flex-1 px-6 py-4 font-medium transition-all ${
              activeTab === 'greeting'
                ? 'text-white border-b-2 border-blue-500 bg-blue-500/10'
                : 'text-gray-400 hover:text-gray-300 hover:bg-gray-800/50'
            }`}
          >
            <div className="flex items-center justify-center gap-2">
              <MessageSquare size={20} />
              <span>Saudação</span>
            </div>
          </button>
          <button
            onClick={() => setActiveTab('away')}
            className={`flex-1 px-6 py-4 font-medium transition-all ${
              activeTab === 'away'
                ? 'text-white border-b-2 border-amber-500 bg-amber-500/10'
                : 'text-gray-400 hover:text-gray-300 hover:bg-gray-800/50'
            }`}
          >
            <div className="flex items-center justify-center gap-2">
              <Moon size={20} />
              <span>Ausência</span>
            </div>
          </button>
          <button
            data-testid="whatsapp-tab-status-messages"
            onClick={() => setActiveTab('status-messages')}
            className={`flex-1 px-6 py-4 font-medium transition-all ${
              activeTab === 'status-messages'
                ? 'text-white border-b-2 border-emerald-500 bg-emerald-500/10'
                : 'text-gray-400 hover:text-gray-300 hover:bg-gray-800/50'
            }`}
          >
            <div className="flex items-center justify-center gap-2">
              <Bell size={20} />
              <span>Mensagens Automáticas</span>
            </div>
          </button>
          <button
            data-testid="whatsapp-tab-status-posts"
            onClick={() => setActiveTab('status-posts')}
            className={`flex-1 px-6 py-4 font-medium transition-all ${
              activeTab === 'status-posts'
                ? 'text-white border-b-2 border-pink-500 bg-pink-500/10'
                : 'text-gray-400 hover:text-gray-300 hover:bg-gray-800/50'
            }`}
          >
            <div className="flex items-center justify-center gap-2">
              <Calendar size={20} />
              <span>Status (Stories)</span>
            </div>
          </button>
        </div>
      </div>

      {loading ? (
        <div className="glass-card flex items-center justify-center py-12">
          <div className="loading-spinner"></div>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Connection Tab */}
          {activeTab === 'connection' && (
            <>
          {/* Status Card */}
          <div className={`glass-card border-2 ${
            status?.connected 
              ? 'border-green-500/30 bg-gradient-to-br from-green-500/10 to-emerald-500/10'
              : 'border-gray-700'
          }`}>
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-xl font-semibold text-white mb-1">
                  Status da Conexão
                </h3>
                <p className="text-gray-400 text-sm">
                  {status?.connected ? '🟢 WhatsApp conectado e funcionando' : 
                   status?.status === 'qrcode' ? '🟡 Aguardando escaneamento do QR Code' :
                   '🔴 Nenhuma instância conectada'}
                </p>
              </div>
              <div className={`w-4 h-4 rounded-full ${
                status?.connected ? 'bg-green-500 animate-pulse' : 
                status?.status === 'qrcode' ? 'bg-yellow-500 animate-pulse' :
                'bg-gray-600'
              }`} />
            </div>

            {status?.connected ? (
              <div className="space-y-4">
                <div className="bg-gray-800/50 rounded-lg p-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <p className="text-gray-400 text-sm mb-1">Instância</p>
                      <p className="text-white font-medium">{status.instance_name}</p>
                    </div>
                    <div>
                      <p className="text-gray-400 text-sm mb-1">Número</p>
                      <p className="text-white font-medium">{status.phone || 'Carregando...'}</p>
                    </div>
                  </div>
                </div>

                <button
                  onClick={handleDisconnect}
                  disabled={disconnecting}
                  className="w-full bg-red-500 hover:bg-red-600 text-white font-semibold py-3 rounded-xl transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {disconnecting ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
                      Desconectando...
                    </>
                  ) : (
                    <>
                      <XCircle size={20} />
                      Desconectar Instância
                    </>
                  )}
                </button>
              </div>
            ) : !status?.connected && !qrCode ? (
              <div className="space-y-4">
                {status?.status === 'device_removed' && (
                  <div className="bg-yellow-500/10 border-2 border-yellow-500/30 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0">
                        <svg className="w-6 h-6 text-yellow-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                        </svg>
                      </div>
                      <div className="flex-1">
                        <h4 className="text-yellow-400 font-semibold mb-1">WhatsApp Desconectado</h4>
                        <p className="text-gray-300 text-sm">A conexão foi perdida. Reconecte escaneando o QR Code ou usando o código pelo número.</p>
                      </div>
                    </div>
                  </div>
                )}
                <div className="bg-slate-900/40 border-2 border-white/10 rounded-xl p-4 space-y-3" data-testid="whatsapp-location-box">
                  <div>
                    <p className="text-sm font-semibold text-white">Selecione sua localização</p>
                    <p className="text-xs text-gray-400">Conectamos por um IP da sua região — evita o aviso de &quot;possível golpe&quot; no WhatsApp dos seus clientes.</p>
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <select value={selectedUF} onChange={(e) => { setSelectedUF(e.target.value); loadCities(e.target.value); }}
                      className="col-span-1 bg-slate-800 border-2 border-white/10 focus:border-green-500/60 outline-none rounded-xl px-3 py-3 text-white" data-testid="whatsapp-uf-select">
                      <option value="">UF</option>
                      {BR_UFS.map((uf) => (<option key={uf} value={uf}>{uf}</option>))}
                    </select>
                    <select value={selectedCity} onChange={(e) => setSelectedCity(e.target.value)} disabled={!selectedUF || loadingCities}
                      className="col-span-2 bg-slate-800 border-2 border-white/10 focus:border-green-500/60 outline-none rounded-xl px-3 py-3 text-white disabled:opacity-50" data-testid="whatsapp-city-select">
                      <option value="">{loadingCities ? 'Carregando cidades...' : (selectedUF ? 'Selecione a cidade' : 'Escolha a UF primeiro')}</option>
                      {proxyCities.map((c) => (<option key={c.value} value={c.value}>{c.label}</option>))}
                    </select>
                  </div>
                </div>
                <button onClick={() => handleConnect()} disabled={connecting || !selectedUF || !selectedCity}
                  className="w-full bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white font-bold py-4 rounded-xl transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                  data-testid="whatsapp-connect-qr-button">
                  {connecting ? (<><div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />Conectando...</>) : (<><MessageSquare size={20} />Conectar com QR Code</>)}
                </button>
                <div className="flex items-center gap-3">
                  <div className="h-px bg-white/15 flex-1" />
                  <span className="text-xs uppercase tracking-widest text-gray-400">ou pelo número</span>
                  <div className="h-px bg-white/15 flex-1" />
                </div>
                <div className="bg-slate-900/40 border-2 border-white/10 rounded-xl p-4 space-y-3">
                  <p className="text-sm text-gray-300">Conecte digitando um código no celular (sem precisar escanear o QR).</p>
                  <div className="flex items-stretch gap-2">
                    <input type="tel" value={pairingPhone} onChange={(e) => setPairingPhone(e.target.value)}
                      placeholder="DDD + número (ex: 11 99999-9999)"
                      className="flex-1 bg-slate-800 border-2 border-white/10 focus:border-green-500/60 outline-none rounded-xl px-4 py-3 text-white placeholder-gray-500 transition-colors"
                      data-testid="whatsapp-pairing-phone-input" />
                    <button onClick={handlePairingConnect} disabled={connecting || !selectedUF || !selectedCity}
                      className="px-5 rounded-xl bg-green-500 hover:bg-green-600 text-white font-semibold transition-colors disabled:opacity-50"
                      data-testid="whatsapp-pairing-generate-button">
                      {connecting ? '...' : 'Gerar código'}
                    </button>
                  </div>
                </div>
              </div>
            ) : null}
          </div>

          {/* QR Code Card */}
          {(qrCode || pairingCode) && !status?.connected && (
            <div className="glass-card border-2 border-yellow-500/30 bg-gradient-to-br from-yellow-500/10 to-orange-500/10">
              <h3 className="text-xl font-semibold text-white mb-4">
                📱 Conecte seu WhatsApp
              </h3>

              {qrCode && (
                <div className="bg-white p-6 rounded-xl mx-auto w-fit mb-4">
                  <img 
                    key={qrCode.substring(0, 50)}
                    src={qrCode}
                    alt="QR Code"
                    className="w-64 h-64"
                  />
                </div>
              )}

              {pairingCode && (
                <div className="max-w-md mx-auto mb-5">
                  {qrCode && (
                    <div className="flex items-center gap-3 my-3">
                      <div className="h-px bg-white/15 flex-1" /><span className="text-xs uppercase tracking-widest text-gray-400">ou</span><div className="h-px bg-white/15 flex-1" />
                    </div>
                  )}
                  <p className="text-sm text-gray-300 text-center mb-2">Use este código de pareamento no WhatsApp:</p>
                  <div className="flex items-stretch gap-2">
                    <div className="flex-1 bg-slate-900/70 border-2 border-yellow-500/40 rounded-xl px-4 py-3 text-center" data-testid="whatsapp-pairing-code-display">
                      <span className="font-mono text-2xl font-bold tracking-[0.4em] text-yellow-300 select-all">{pairingCode}</span>
                    </div>
                    <button type="button" onClick={async () => { try { await navigator.clipboard.writeText(pairingCode); setCopiedPairing(true); toast.success('Código copiado!'); setTimeout(() => setCopiedPairing(false), 2000); } catch (_) { toast.error('Não foi possível copiar.'); } }}
                      className="px-4 rounded-xl bg-yellow-500 hover:bg-yellow-600 text-slate-900 font-semibold text-sm transition-colors" data-testid="whatsapp-pairing-code-copy">
                      {copiedPairing ? '✓' : 'Copiar'}
                    </button>
                  </div>
                  <p className="text-[11px] text-gray-500 text-center mt-2">No WhatsApp do celular: Menu → Aparelhos conectados → Conectar um aparelho → Conectar com número de telefone</p>
                </div>
              )}

              <div className="text-center space-y-2">
                <p className="text-yellow-400 font-medium">
                  🔄 Aguardando leitura do QR Code...
                </p>
                <p className="text-gray-300 text-sm">
                  Abra o WhatsApp no seu celular e escaneie este código
                </p>
                <p className="text-gray-400 text-xs">
                  Verificando conexão automaticamente a cada 3 segundos...
                </p>
                <button
                  type="button"
                  onClick={handleRefreshStatus}
                  disabled={refreshingStatus}
                  data-testid="whatsapp-refresh-status-button"
                  className="mt-3 inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-600 disabled:opacity-60 disabled:cursor-not-allowed text-white font-semibold px-5 py-2.5 rounded-xl transition-colors"
                >
                  {refreshingStatus ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                      Verificando...
                    </>
                  ) : (
                    <>
                      <RefreshCw size={16} />
                      Já escaneei, verificar agora
                    </>
                  )}
                </button>
              </div>
            </div>
          )}

          {/* Test Message Card - Only show when NOT connected (no Evolution API) */}
          {(() => {
            const shouldShow = !status?.connected && status?.status === 'disconnected';
            console.log('🔍 DEBUG - Mensagem Manual:', {
              connected: status?.connected,
              status: status?.status,
              shouldShow: shouldShow,
              fullStatus: status
            });
            return shouldShow;
          })() && (
            <div className="glass-card">
              <h3 className="text-xl font-semibold text-white mb-4">
                📱 Enviar Mensagem Manual
              </h3>
              <p className="text-sm text-gray-400 mb-4">
                ⚠️ WhatsApp não está conectado via API. Use este formulário para enviar mensagens manualmente.
              </p>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Número (com DDI e DDD)
                  </label>
                  <input
                    type="tel"
                    value={testMessage.number}
                    onChange={(e) => setTestMessage({...testMessage, number: e.target.value.replace(/\D/g, '')})}
                    placeholder="5531999999999"
                    className="glass-input w-full"
                    maxLength="13"
                  />
                  <p className="text-xs text-gray-400 mt-1">
                    ⚠️ <strong>Importante:</strong> Digite um número válido e registrado no WhatsApp
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    Formato: 5531999999999 (55 = Brasil, 31 = DDD, 999999999 = número com 9 dígitos)
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Mensagem
                  </label>
                  <textarea
                    value={testMessage.text}
                    onChange={(e) => setTestMessage({...testMessage, text: e.target.value})}
                    rows={3}
                    className="glass-input w-full resize-none"
                    placeholder="Digite sua mensagem de teste..."
                  />
                </div>

                <button
                  onClick={handleSendTest}
                  disabled={sendingTest || !testMessage.number || !testMessage.text}
                  className="w-full bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white font-bold py-3 rounded-xl transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {sendingTest ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
                      Enviando...
                    </>
                  ) : (
                    <>
                      <MessageSquare size={20} />
                      Enviar Mensagem
                    </>
                  )}
                </button>
              </div>
            </div>
          )}

          {/* Instructions */}
          <div className="glass-card">
            <h3 className="text-lg font-semibold text-white mb-3">📋 Como Conectar</h3>
            <ol className="space-y-2 text-gray-300">
              <li className="flex items-start gap-3">
                <span className="flex-shrink-0 w-6 h-6 bg-green-500/20 text-green-400 rounded-full flex items-center justify-center text-sm font-bold">1</span>
                <span>Clique em &quot;Conectar WhatsApp&quot;</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="flex-shrink-0 w-6 h-6 bg-green-500/20 text-green-400 rounded-full flex items-center justify-center text-sm font-bold">2</span>
                <span>Aguarde o QR Code ser gerado</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="flex-shrink-0 w-6 h-6 bg-green-500/20 text-green-400 rounded-full flex items-center justify-center text-sm font-bold">3</span>
                <span>Abra o WhatsApp no seu celular → Configurações → Aparelhos conectados → Conectar um aparelho</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="flex-shrink-0 w-6 h-6 bg-green-500/20 text-green-400 rounded-full flex items-center justify-center text-sm font-bold">4</span>
                <span>Escaneie o QR Code exibido</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="flex-shrink-0 w-6 h-6 bg-green-500/20 text-green-400 rounded-full flex items-center justify-center text-sm font-bold">5</span>
                <span>Aguarde a confirmação da conexão (automática)</span>
              </li>
            </ol>
          </div>

          {/* Info Card */}
          <div className="glass-card bg-gradient-to-br from-blue-500/10 to-cyan-500/10 border-2 border-blue-500/30">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-blue-500/20 flex items-center justify-center">
                <svg className="w-5 h-5 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <h4 className="text-white font-semibold mb-1">💡 Informações Importantes</h4>
                <ul className="text-gray-300 text-sm space-y-1">
                  <li>• Cada loja pode ter apenas 1 instância ativa</li>
                  <li>• A conexão é gerenciada de forma automática e segura</li>
                  <li>• O nome da instância é gerado automaticamente</li>
                  <li>• Use a mensagem de teste para verificar o funcionamento</li>
                </ul>
              </div>
            </div>
          </div>
          </>
          )}

          {/* AI Configuration Tab */}
          {activeTab === 'ai' && (
            <WhatsAppAITab
              aiConfig={aiConfig}
              setAiConfig={setAiConfig}
              savedAiConfig={savedAiConfig}
              savingAi={savingAi}
              showAiKey={showAiKey}
              setShowAiKey={setShowAiKey}
              reloadingAi={reloadingAi}
              uploadingMenuImage={uploadingMenuImage}
              setUploadingMenuImage={setUploadingMenuImage}
              handleSaveAiConfig={handleSaveAiConfig}
              handleReloadAi={handleReloadAi}
            />
          )}

          {/* Greeting Message Tab */}
          {activeTab === 'greeting' && (
            <GreetingMessageSettings />
          )}

          {/* Away Message Tab */}
          {activeTab === 'away' && (
            <AwayMessageSettings />
          )}

          {/* Status Messages Tab (NOVA) */}
          {activeTab === 'status-messages' && (
            <>
              {!status?.connected && (
                <div className="rounded-xl border border-amber-500/30 bg-amber-950/30 p-4 flex items-start gap-3 mb-4">
                  <AlertCircle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-amber-200 text-sm font-medium">
                      WhatsApp não está conectado
                    </p>
                    <p className="text-amber-100/80 text-xs mt-1">
                      Você pode editar e salvar as mensagens agora, mas elas só serão enviadas aos clientes quando você conectar o WhatsApp na aba <strong>&quot;Conexão WhatsApp&quot;</strong>.
                    </p>
                    <button
                      onClick={() => setActiveTab('connection')}
                      className="mt-2 text-xs text-amber-200 hover:text-white underline"
                    >
                      Ir para Conexão WhatsApp →
                    </button>
                  </div>
                </div>
              )}
              <StatusMessagesPanel />
            </>
          )}

          {/* Status Posts (Stories) Tab */}
          {activeTab === 'status-posts' && (
            <StatusPostsManager connected={status?.connected} goToConnection={() => setActiveTab('connection')} />
          )}
        </div>
      )}
    </div>
  );
};
export default WhatsAppContent;
